import type {
  EntitySupplementVisibilitySettings,
  ResponseBaseGetOneResponseEntitySupplementTag,
} from "@classes-service-types/index.ts";
import { datadogLogs } from "@datadog/browser-logs";
import type { UseMutationOptions } from "@tanstack/vue-query";
import { useMutation, useQueryClient } from "@tanstack/vue-query";
import type { Ref } from "vue";
import { unref } from "vue";
import entityVisibilityService from "@/services/entityVisibility.js";
import { getEntitySupplementQueryKey } from "../entitySupplement/useGetSingleEntitySupplement";

type UseResetVisibilityRulesArgs = {
  entityId: Ref<string>;
  payload: EntitySupplementVisibilitySettings;
};

export const useResetVisibilityRules = (
  useMutationOptions?: UseMutationOptions<
    ResponseBaseGetOneResponseEntitySupplementTag,
    Error,
    UseResetVisibilityRulesArgs
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation<ResponseBaseGetOneResponseEntitySupplementTag, Error, UseResetVisibilityRulesArgs>({
    mutationFn: async ({ entityId, payload }) => {
      const response = await entityVisibilityService.resetVisibilityRules(unref(entityId), payload);
      return response.data;
    },
    onSuccess: async (data, { entityId }) => {
      await queryClient.invalidateQueries({
        queryKey: getEntitySupplementQueryKey(entityId),
      });
    },
    onError: (error, variables) => {
      datadogLogs.logger.warn("Failed to reset visibility rules.", {
        error: error.message,
        entityId: unref(variables.entityId),
      });
    },
    ...useMutationOptions,
  });
};
